import React, { useEffect, useState } from "react";
import { Row, Col, Modal, Button, Form, Spinner } from "react-bootstrap";
import Card from "../../../components/Card";
import { toast } from "react-toastify";
import { GoldAPI } from "../../../js/api";
import { handleApiError } from "../../../js/helper/errorHandler";
import DataTable from "../components/DataTable/DataTable";
import Swal from "sweetalert2";

const GoldRates = () => {
  const [goldList, setGoldList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // table states
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [addModal, setAddModal] = useState({
    show: false,
    row: { purity: "", price_per_gram: "" },
  });

  const [editModal, setEditModal] = useState({
    show: false,
    row: null,
  });

  const columns = [
    { key: "index", label: "No." },
    { key: "purity", label: "Purity" },
    { key: "price_per_gram", label: "Price / Gram (₹)" },
    { key: "updated_at", label: "Last Updated" },
    { key: "action", label: "Action" },
  ];

  const fetchGoldRates = async () => {
    try {
      setIsLoading(true);
      const res = await GoldAPI.getGoldRates();

      if (res.data.status === 200) {
        setGoldList(res.data.data.rates || []);
      } else {
        handleApiError(res, "Fetching Gold Rates");
      }
    } catch (err) {
      handleApiError(err, "Fetching Gold Rates");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGoldRates();
  }, []);

  const handleCreateGoldRate = async () => {
    const { purity, price_per_gram } = addModal.row;

    if (!purity || !price_per_gram) {
      return toast.error("All fields are required.");
    }

    try {
      setIsLoading(true);
      const res = await GoldAPI.addGoldRate({
        purity,
        price_per_gram: Number(price_per_gram),
      });

      if (res.data.status === 200) {
        toast.success("Gold rate added!");
        setAddModal({ show: false, row: { purity: "", price_per_gram: "" } });
        fetchGoldRates();
      } else {
        handleApiError(res, "Creating Gold Rate");
      }
    } catch (err) {
      handleApiError(err, "Creating Gold Rate");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateRate = async () => {
    try {
      setIsLoading(true);

      const res = await GoldAPI.updateGoldRate(editModal.row._id, {
        price_per_gram: Number(editModal.row.price_per_gram),
      });

      if (res.data.status === 200) {
        toast.success("Rate updated!");
        setEditModal({ show: false, row: null });
        fetchGoldRates();
      } else {
        handleApiError(res, "Updating Gold Rate");
      }
    } catch (err) {
      handleApiError(err, "Updating Gold Rate");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRate = async (id) => {
    const result = await Swal.fire({
      title: "Delete Gold Rate?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      setIsLoading(true);

      const res = await GoldAPI.deleteGoldRate(id);

      if (res.data.status === 200) {
        Swal.fire("Deleted!", "Gold rate has been deleted.", "success");
        fetchGoldRates();
      } else {
        handleApiError(res, "Deleting Gold Rate");
      }
    } catch (err) {
      handleApiError(err, "Deleting Gold Rate");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetLivePrice = async () => {
    try {
      setIsLoading(true);
      const res = await GoldAPI.getGoldRatesWithApi();

      if (res.data.status === 200) {
        toast.success("Live gold price updated!");
        fetchGoldRates();
      } else {
        handleApiError(res, "Fetching Live Gold Price");
      }
    } catch (err) {
      handleApiError(err, "Fetching Live Gold Price");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Gold Rates</h4>

              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-warning"
                  className="d-inline-flex align-items-center gap-1"
                  onClick={handleGetLivePrice}
                >
                  <i className="ri-refresh-line"></i>
                  Get Live Price
                </Button>

                <Button
                  size="sm"
                  variant="primary"
                  onClick={() =>
                    setAddModal({
                      show: true,
                      row: { purity: "", price_per_gram: "" },
                    })
                  }
                >
                  <i className="ri-add-line me-1"></i>
                  Add Rate
                </Button>
              </div>
            </Card.Header>

            <Card.Body style={{ position: "relative" }}>
              {isLoading && (
                <Spinner
                  animation="border"
                  style={{ position: "absolute", top: "50%", left: "50%" }}
                />
              )}

              <DataTable
                columns={columns}
                data={goldList}
                search={search}
                onSearch={setSearch}
                page={page}
                totalPages={1}
                limit={limit}
                onLimitChange={setLimit}
                onPageChange={setPage}
                renderRow={(p, i) => (
                  <tr key={p._id}>
                    <td>{(page - 1) * limit + i + 1}</td>
                    <td>{p.purity} </td>
                    <td>₹{Math.round(p.price_per_gram)}</td>
                    <td>{new Date(p.updatedAt).toLocaleString()}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="primary"
                        className="me-2"
                        onClick={() => setEditModal({ show: true, row: p })}
                      >
                        Edit
                      </Button>

                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDeleteRate(p._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                )}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* EDIT MODAL */}
      <Modal
        show={editModal.show}
        onHide={() => setEditModal({ show: false, row: null })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Gold Rate</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editModal.row && (
            <Form.Control
              type="number"
              value={editModal.row.price_per_gram}
              onChange={(e) =>
                setEditModal({
                  ...editModal,
                  row: { ...editModal.row, price_per_gram: e.target.value },
                })
              }
            />
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditModal({ show: false, row: null })}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateRate}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ADD MODAL */}
      <Modal
        show={addModal.show}
        onHide={() =>
          setAddModal({ show: false, row: { purity: "", price_per_gram: "" } })
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Gold Rate</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Purity</Form.Label>
            <Form.Control
              value={addModal.row.purity}
              onChange={(e) =>
                setAddModal({
                  ...addModal,
                  row: { ...addModal.row, purity: e.target.value },
                })
              }
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Price / Gram (₹)</Form.Label>
            <Form.Control
              type="number"
              value={addModal.row.price_per_gram}
              onChange={(e) =>
                setAddModal({
                  ...addModal,
                  row: { ...addModal.row, price_per_gram: e.target.value },
                })
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setAddModal({
                show: false,
                row: { purity: "", price_per_gram: "" },
              })
            }
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateGoldRate}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GoldRates;
